/*
 * Cart platform picker + upgrade-code auto-apply.
 *
 * Markup comes from snippets/platform-picker.liquid inside the /cart form
 * (form[data-si-cart]). Ajaxify loads that same form into the cart modal, so
 * everything here is delegated or re-run when the modal is reprinted.
 * The product page nav bar uses the same snippet; there choosing a platform
 * only updates the shown price, including upgrade pricing (the radios submit
 * with Buy Now).
 *
 * - Changing a format radio swaps the line's variant and moves its upgrade code.
 * - After each render, the cart's discount codes are synced with the upgrade
 *   codes the Liquid marked as earned (data-current-code). Other codes are kept.
 * - A code Shopify won't apply is remembered for the session and not retried.
 */
(function () {
  'use strict';

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var REJECTED_KEY = 'si-upgrade-rejected';
  var attempted = {};
  var lastModalForm = null;

  function warn(err) {
    if (window.console) console.warn('[cart-upgrades]', err);
  }

  function prefixOf(form) {
    return (form.getAttribute('data-code-prefix') || 'UP-').toUpperCase();
  }

  function isUpgrade(code, prefix) {
    return code.indexOf(prefix) === 0;
  }

  function uniq(list) {
    var seen = {};
    return list.filter(function (c) {
      if (!c || seen[c]) return false;
      seen[c] = true;
      return true;
    });
  }

  function sameSet(a, b) {
    a = uniq(a).sort();
    b = uniq(b).sort();
    return a.join(',') === b.join(',');
  }

  function loadRejected() {
    try {
      return JSON.parse(window.sessionStorage.getItem(REJECTED_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function saveRejected(rejected) {
    try {
      window.sessionStorage.setItem(REJECTED_KEY, JSON.stringify(rejected));
    } catch (e) {}
  }

  function getCart() {
    return fetch(root + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  function updateCart(body) {
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('update.js ' + res.status);
      return res.json();
    });
  }

  // Codes on the cart, upper-cased, mapped to whether they apply.
  // Newer cart JSON lists them in discount_codes; otherwise fall back to the
  // discount applications that came from codes (those all apply).
  function codeStates(cart) {
    var states = {};
    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (d) {
        if (d && d.code) states[String(d.code).toUpperCase()] = d.applicable !== false;
      });
      return states;
    }
    function take(app) {
      if (app && app.type === 'discount_code' && app.title) states[String(app.title).toUpperCase()] = true;
    }
    (cart.cart_level_discount_applications || []).forEach(take);
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        take(a.discount_application);
      });
    });
    return states;
  }

  // Upgrade codes the rendered cart says this customer has earned (one per line)
  function earnedCodes(form) {
    var codes = [];
    var marks = form.querySelectorAll('[data-current-code]');
    for (var i = 0; i < marks.length; i++) {
      var code = (marks[i].getAttribute('data-current-code') || '').toUpperCase();
      if (code) codes.push(code);
    }
    return uniq(codes);
  }

  // Keep every non-upgrade code already on the cart, swap in the earned upgrade codes
  function mergeCodes(applied, earned, prefix) {
    var others = applied.filter(function (c) { return !isUpgrade(c, prefix); });
    return uniq(others.concat(earned));
  }

  // Put `codes` on the cart. Any earned upgrade code Shopify refuses (listed as
  // not applicable, or dropped) is remembered and taken straight back off.
  // Resolves to the cart's final code states.
  function applyCodes(codes, earned, rejected) {
    return updateCart({ discount: codes.join(',') }).then(function (cart) {
      var states = codeStates(cart);
      var refused = earned.filter(function (c) { return states[c] !== true; });
      if (!refused.length) return states;
      refused.forEach(function (c) { rejected[c] = true; });
      saveRejected(rejected);
      var kept = codes.filter(function (c) { return refused.indexOf(c) === -1; });
      return updateCart({ discount: kept.join(',') }).then(codeStates);
    });
  }

  function refresh(form) {
    if (form.closest('#AjaxifyCart') && window.ajaxifyShopify && ajaxifyShopify.refresh) {
      ajaxifyShopify.refresh();
    } else {
      window.location.reload();
    }
  }

  function sync(form) {
    if (!form || form.siSyncing) return;
    form.siSyncing = true;

    var prefix = prefixOf(form);
    var rejected = loadRejected();

    getCart().then(function (cart) {
      var states = codeStates(cart);
      Object.keys(states).forEach(function (c) {
        if (isUpgrade(c, prefix) && states[c] === false) rejected[c] = true;
      });
      saveRejected(rejected);

      var before = Object.keys(states);
      var earned = earnedCodes(form).filter(function (c) { return !rejected[c]; });
      var next = mergeCodes(before, earned, prefix);
      var signature = next.slice().sort().join(',');
      if (sameSet(before, next) || attempted[signature]) return null;
      attempted[signature] = true;

      return applyCodes(next, earned, rejected).then(function (after) {
        if (!sameSet(before, Object.keys(after))) refresh(form);
      });
    }).catch(warn).then(function () {
      form.siSyncing = false;
    });
  }

  function setBusy(picker, busy) {
    picker.classList.toggle('is-loading', busy);
    var radios = picker.querySelectorAll('input[data-si-format]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].disabled = busy || radios[i].hasAttribute('data-locked');
    }
  }

  function onFormatChange(radio) {
    var picker = radio.closest('[data-si-picker]');
    var form = radio.closest('form[data-si-cart]');
    if (!picker || !form) return;

    var oldId = picker.getAttribute('data-current-variant');
    var newId = radio.value;
    if (oldId === newId) return;

    var prefix = prefixOf(form);
    var rejected = loadRejected();
    var oldCode = (picker.getAttribute('data-current-code') || '').toUpperCase();
    var newCode = (radio.getAttribute('data-code') || '').toUpperCase();
    var error = picker.querySelector('[data-si-error]');
    if (error) error.hidden = true;
    setBusy(picker, true);

    var updates = {};
    updates[oldId] = 0;
    updates[newId] = 1;

    updateCart({ updates: updates }).then(function (cart) {
      var earned = earnedCodes(form).filter(function (c) { return c !== oldCode && !rejected[c]; });
      if (newCode && !rejected[newCode]) earned.push(newCode);
      var applied = Object.keys(codeStates(cart));
      var next = mergeCodes(applied, earned, prefix);
      if (sameSet(applied, next)) return null;
      return applyCodes(next, earned, rejected);
    }).then(function () {
      refresh(form);
    }).catch(function (err) {
      warn(err);
      setBusy(picker, false);
      var current = picker.querySelector('input[data-si-format][value="' + oldId + '"]');
      if (current) current.checked = true;
      if (error) error.hidden = false;
    });
  }

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.matches) return;
    if (target.matches('input[data-si-format]')) onFormatChange(target);
    else if (target.matches('[data-si-product-picker] input')) showPlatform(target);
  });

  // Product page nav bar: show the chosen platform's price. An upgrade shows like a
  // sale (list price struck through, upgrade price after it); otherwise any sale.
  function showPlatform(radio) {
    var bar = radio.closest('#product-nav-bar') || document;
    var price = bar.querySelector('[data-si-price]');
    var compare = bar.querySelector('[data-si-compare]');
    var upgrade = radio.getAttribute('data-upgrade-price');
    var struck = upgrade ? radio.getAttribute('data-list-price') : radio.getAttribute('data-compare-price');
    if (price && radio.getAttribute('data-price')) price.textContent = upgrade || radio.getAttribute('data-price');
    if (compare) {
      compare.textContent = struck || '';
      compare.hidden = !struck;
    }
  }

  // Cart page: sync once the page form is there
  function syncPageForm() {
    var forms = document.querySelectorAll('form[data-si-cart]');
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].closest('#AjaxifyCart')) sync(forms[i]);
    }
  }

  // Cart modal: Ajaxify replaces #AjaxifyCart's contents on every reprint
  var pending = null;
  function checkModal() {
    pending = null;
    var form = document.querySelector('#AjaxifyCart form[data-si-cart]');
    if (form && form !== lastModalForm) {
      lastModalForm = form;
      sync(form);
    }
  }

  function start() {
    syncPageForm();
    // The nav bar prints the product's lowest price; match the preselected platform
    var chosen = document.querySelector('[data-si-product-picker] input:checked');
    if (chosen) showPlatform(chosen);
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (!pending) pending = setTimeout(checkModal, 50);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
